import { TYPE_LABELS, skeletonShape } from './visualTypes.js';
import { TypeBadge } from './typeBadge';

// Hauteurs de barres du skeleton (silhouette stable, sans aléatoire entre deux rendus)
const BARS = [46, 68, 54, 82, 62, 74, 58];
const HBARS = [92, 78, 66, 52, 40];
// Planning : [ligne, début %, largeur %] (silhouette stable)
const PLAN_BARS = [[0, 8, 30], [0, 52, 22], [1, 20, 40], [2, 4, 18], [2, 36, 34], [3, 58, 30], [4, 14, 26]];
// Repères de la silhouette de carte : [x %, y %]
const PINS = [[22, 38], [41, 62], [58, 30], [72, 55], [33, 22], [84, 34]];

const PIN_PATH = 'M12 2a7 7 0 0 0-7 7c0 5.2 7 13 7 13s7-7.8 7-13a7 7 0 0 0-7-7Z';

/**
 * Silhouette d'une carte en chargement : fond, routes et repères (ou tracé A → B).
 * Aussi affichée pendant le téléchargement du moteur cartographique.
 */
export function MapSilhouette({ route = false }) {
  return (
    <div className="viz-skel__map" aria-hidden="true">
      <svg className="viz-skel__roads" viewBox="0 0 300 160" preserveAspectRatio="none">
        <path d="M0 120 C60 100 90 130 150 96 S250 60 300 74" />
        <path d="M40 0 C60 50 40 90 80 160" />
        <path d="M210 0 C200 60 240 100 230 160" />
      </svg>
      {route ? (
        <svg className="viz-skel__route" viewBox="0 0 300 160" preserveAspectRatio="none">
          <path d="M48 118 C90 60 150 132 190 82 S240 40 252 42" />
          <circle cx="48" cy="118" r="7" />
          <circle cx="252" cy="42" r="7" />
        </svg>
      ) : (
        PINS.map(([x, y], i) => (
          <svg key={i} className="viz-skel__pin" style={{ left: `${x}%`, top: `${y}%` }} viewBox="0 0 24 24">
            <path d={PIN_PATH} />
          </svg>
        ))
      )}
    </div>
  );
}

function Silhouette({ shape }) {
  switch (shape) {
    case 'bars':
      return (
        <div className="viz-skel__bars">
          {BARS.map((h, i) => <span key={i} className="viz-skel__fill" style={{ height: `${h}%` }} />)}
        </div>
      );
    case 'hbars':
      return (
        <div className="viz-skel__hbars">
          {HBARS.map((w, i) => (
            <span key={i} className="viz-skel__hrow">
              <span className="viz-skel__fill viz-skel__label" />
              <span className="viz-skel__fill" style={{ width: `${w}%` }} />
            </span>
          ))}
        </div>
      );
    case 'line':
      return (
        <svg className="viz-skel__svg" viewBox="0 0 300 120" preserveAspectRatio="none" aria-hidden="true">
          <path className="viz-skel__stroke" d="M4 92 L52 70 L100 78 L148 44 L196 56 L244 30 L296 38" />
          <path className="viz-skel__axis" d="M4 116 H296" />
        </svg>
      );
    case 'donut':
      return (
        <svg className="viz-skel__svg viz-skel__svg--square" viewBox="0 0 120 120" aria-hidden="true">
          <circle className="viz-skel__ring" cx="60" cy="60" r="40" />
        </svg>
      );
    case 'gauge':
      return (
        <svg className="viz-skel__svg viz-skel__svg--gauge" viewBox="0 0 160 100" aria-hidden="true">
          <path className="viz-skel__ring" d="M20 90 A60 60 0 1 1 140 90" />
        </svg>
      );
    case 'tiles':
      return (
        <div className="viz-skel__tiles">
          {[0, 1, 2].map((i) => (
            <span key={i} className="viz-skel__tile">
              <span className="viz-skel__fill viz-skel__line is-short" />
              <span className="viz-skel__fill viz-skel__line is-big" />
              <span className="viz-skel__fill viz-skel__line" />
            </span>
          ))}
        </div>
      );
    case 'table':
      return (
        <div className="viz-skel__table">
          {[0, 1, 2, 3, 4].map((i) => <span key={i} className={`viz-skel__fill viz-skel__trow ${i === 0 ? 'is-head' : ''}`} />)}
        </div>
      );
    case 'map':
      return <MapSilhouette />;
    case 'route':
      return <MapSilhouette route />;
    case 'planning':
      return (
        <div className="viz-skel__plan">
          <span className="viz-skel__plan-head">
            {[0, 1, 2, 3, 4, 5].map((i) => <span key={i} className="viz-skel__fill viz-skel__plan-tick" />)}
          </span>
          {[0, 1, 2, 3, 4].map((row) => (
            <span key={row} className="viz-skel__plan-row">
              <span className="viz-skel__fill viz-skel__label" />
              <span className="viz-skel__plan-track">
                {PLAN_BARS.filter(([r]) => r === row).map(([, left, width], i) => (
                  <span key={i} className="viz-skel__fill viz-skel__plan-bar" style={{ left: `${left}%`, width: `${width}%` }} />
                ))}
              </span>
            </span>
          ))}
        </div>
      );
    case 'embed':
      return (
        <div className="viz-skel__embed">
          <span className="viz-skel__embed-bar">
            <span className="viz-skel__fill viz-skel__embed-dot" />
            <span className="viz-skel__fill viz-skel__embed-url" />
          </span>
          <span className="viz-skel__fill viz-skel__embed-page" />
        </div>
      );
    default:
      return <div className="viz-skel__fill viz-skel__generic" />;
  }
}

const PENDING_LABELS = { map: 'Préparation de la carte…', route: "Calcul de l'itinéraire…", planning: 'Préparation du planning…', embed: 'Vérification de la page…' };
const KIND_LABELS = { map: 'Carte', planning: 'Planning', embed: 'Page web' };
// Famille annoncée sans type de visuel : teinte et icône d'un type de la famille
const KIND_TYPES = { map: 'markers', planning: 'planning', embed: 'embed' };

/**
 * Skeleton d'un visuel en préparation (`artifact_pending`) : silhouette du type annoncé
 * (barres, courbe, anneau, jauge, tuiles, tableau ; générique si le type est inconnu),
 * à la place exacte où le visuel apparaîtra. Reflet animé coupé en mouvement réduit.
 */
export function VisualSkeleton({ visualType = null, kind = null, title = null }) {
  const shape = skeletonShape(visualType, kind);
  const typeLabel = TYPE_LABELS[visualType] ?? KIND_LABELS[kind] ?? 'Visuel';
  const badgeType = TYPE_LABELS[visualType] ? visualType : KIND_TYPES[kind] ?? null;
  return (
    <div className="viz-card viz-card--pending" role="status" aria-live="polite">
      <div className="viz-card__header">
        <TypeBadge visualType={badgeType} label={typeLabel} className="viz-card__badge" />
        <div className="viz-card__titles">
          {title
            ? <p className="viz-card__title">{title}</p>
            : <span className="viz-skel__fill viz-skel__line is-title" aria-hidden="true" />}
          <span className="viz-card__pending-label">{PENDING_LABELS[shape] ?? 'Préparation du visuel…'}</span>
        </div>
      </div>
      <div className={`viz-skel viz-skel--${shape}`} aria-hidden="true">
        <Silhouette shape={shape} />
      </div>
    </div>
  );
}
